import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { EmptyState } from "../../components/EmptyState";
import { KeepoLoader } from "../../components/KeepoLoader";
import { Box } from "../../components/Layout";
import { PageTitle } from "../../components/PageTitle";
import { Skeleton } from "../../components/Skeleton";
import { useAppState } from "../../contexts/App";
import { Reaction, Release, listReleases, reactToRelease, releasesStatus } from "../../services/releases";
import { sendSuggestion } from "../../services/suggestions";
import { ReleaseCard } from "./ReleaseCard";
import { FloatingRoom, FloatingSuggest, Intro, List, LoadingMore, LoadingScreen, Note, SuggestButton } from "./styles";
import SuggestionModal from "./SuggestionModal";

const prefix = "pages.releases";

/**
 * Novidades: what Keepo launched and improved, newest first, the same as keepo.bio/novidades, with a face to tell what
 * the person thought of each. Opening it clears the menu's dot. 10 at a time, the next page as the list's end shows.
 */
const ReleasesPage = () => {
  const { t, i18n } = useTranslation();
  const [releases, setReleases] = useState<Release[]>();
  const [lastPage, setLastPage] = useState(1);
  const [page, setPage] = useState(1);
  // the language they came in: the reactions send it back
  const [lang, setLang] = useState(i18n.language);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasError, setError] = useState(false);
  // the reactions being sent, and what was said after one (by release)
  const [reacting, setReacting] = useState<Record<string, boolean>>({});
  const [statuses, setStatuses] = useState<Record<string, string>>({});
  const [suggesting, setSuggesting] = useState(false);
  // the Keepo open in the editor, if any: goes with the suggestion
  const { appState } = useAppState();

  // Seen once the list shows (a list that didn't load wasn't seen)
  const markedSeen = useRef(false);
  const markSeen = () => {
    if (!markedSeen.current) {
      markedSeen.current = true;
      releasesStatus.seen();
    }
  };

  const load = useCallback(() => {
    let current = true;
    setLoading(true);
    setError(false);
    listReleases(1, i18n.language)
      .then(list => {
        if (!current) return;
        setReleases(list.items);
        setLastPage(list.meta?.last_page ?? 1);
        setLang(list.lang);
        setPage(1);
        markSeen();
      })
      .catch(() => current && setError(true))
      .finally(() => current && setLoading(false));
    return () => {
      current = false;
    };
  }, [i18n.language]);

  useEffect(load, [load]);

  const loadMore = useCallback(() => {
    setLoadingMore(true);
    setError(false);
    listReleases(page + 1, i18n.language)
      .then(list => {
        setReleases(shown => [
          ...(shown ?? []),
          ...list.items.filter(item => !shown?.some(release => release.slug === item.slug)),
        ]);
        setLastPage(list.meta?.last_page ?? 1);
        setPage(page + 1);
      })
      .catch(() => setError(true))
      .finally(() => setLoadingMore(false));
  }, [page, i18n.language]);

  const hasMore = !!releases && page < lastPage && !loading && !hasError;
  const canWatchEnd = typeof window.IntersectionObserver === "function";
  const listEnd = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const end = listEnd.current;
    if (!hasMore || loadingMore || !canWatchEnd || !end) return;
    const observer = new IntersectionObserver(
      entries => {
        if (entries.some(entry => entry.isIntersecting)) {
          observer.disconnect();
          loadMore();
        }
      },
      { rootMargin: "300px" }
    );
    observer.observe(end);
    return () => observer.disconnect();
  }, [hasMore, loadingMore, canWatchEnd, loadMore]);

  const setReaction = (slug: string, reaction: Reaction | null) =>
    setReleases(shown => shown?.map(item => (item.slug === slug ? { ...item, reaction } : item)));

  // The face is marked at once; if the reaction doesn't go, the one before comes back
  const react = (release: Release, reaction: Reaction) => {
    const before = release.reaction;
    setReaction(release.slug, reaction);
    setReacting(sending => ({ ...sending, [release.slug]: true }));
    reactToRelease(release.slug, reaction, lang)
      .then(() => setStatuses(shown => ({ ...shown, [release.slug]: t(`${prefix}.reactions.thanks`) })))
      .catch(() => {
        setReaction(release.slug, before);
        setStatuses(shown => ({ ...shown, [release.slug]: t(`${prefix}.reactions.error`) }));
      })
      .finally(() => setReacting(sending => ({ ...sending, [release.slug]: false })));
  };

  const errorNote = (
    <Note role="alert" className="mt-4">
      {t(`${prefix}.loadError`)}{" "}
      <button type="button" onClick={releases ? loadMore : load}>
        {t(`${prefix}.retry`)}
      </button>
    </Note>
  );

  if (!releases && loading && !hasError) {
    return (
      <Box>
        <LoadingScreen role="status" aria-label={t(`${prefix}.loading`)}>
          <Skeleton className="title" />
          <Skeleton className="card" />
          <Skeleton className="card" />
        </LoadingScreen>
      </Box>
    );
  }

  return (
    <Box>
      <PageTitle>{t(`${prefix}.title`)}</PageTitle>
      <Intro>{t(`${prefix}.intro`)}</Intro>
      <FloatingSuggest>
        <SuggestButton type="button" onClick={() => setSuggesting(true)}>
          <span aria-hidden="true">💡</span>
          {t(`${prefix}.suggestion.open`)}
        </SuggestButton>
      </FloatingSuggest>
      {suggesting && (
        <SuggestionModal
          onSend={message => sendSuggestion(message, i18n.language, appState.editingSiteId)}
          onClose={() => setSuggesting(false)}
        />
      )}

      {hasError && !releases && errorNote}

      {releases && (
        <List>
          {releases.length === 0 && !hasError && (
            <EmptyState emoji="🗞️" title={t(`${prefix}.emptyTitle`)} description={t(`${prefix}.emptyText`)} />
          )}
          {releases.map(release => (
            <ReleaseCard
              key={release.slug}
              release={release}
              reacting={reacting[release.slug]}
              status={statuses[release.slug]}
              onReact={reaction => react(release, reaction)}
            />
          ))}
        </List>
      )}

      <div ref={listEnd} />
      {/* the next page that didn't come: where the person is, at the list's end */}
      {hasError && releases && errorNote}
      {loadingMore && (
        <LoadingMore role="status" aria-label={t(`${prefix}.loadingMore`)}>
          <KeepoLoader size={20} color="var(--color-neutral-white)" />
        </LoadingMore>
      )}
      {hasMore && !canWatchEnd && !loadingMore && (
        <Note className="text-center">
          <button type="button" onClick={loadMore}>
            {t(`${prefix}.loadMore`)}
          </button>
        </Note>
      )}
      <FloatingRoom />
    </Box>
  );
};

export default ReleasesPage;
